import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { api, MSG_EMERGENCIA } from '../../lib/api';
import { useEmergencia } from '../../lib/emergencia';
import { useAuth } from '../../lib/auth';
import { ComboBox } from '../../components/ComboBox';
import { useToast } from '../../components/Toast';
import { EstoqueEfetivo } from '../../components/EstoqueEfetivo';
import { EditorDataCompra, type AlvoEdicaoData } from '../../components/EditorDataCompra';
import { SeloChegando } from '../../components/SeloChegando';
import { formatMoeda, formatMoedaSemSimbolo, formatMargem, formatDataSP, abaixoDaMeta, compararDescricaoNatural } from '@fiodecobre/shared';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { useGridNavegavel } from '../../lib/useGridNavegavel';
import { ModalPrecosLote } from './ModalPrecosLote';
import { ModalCategoriaLote } from './ModalCategoriaLote';
import { ModalVendas30d } from '../cotacoes/MaisVendidos';
import { ModalSelecionarCotacao } from '../cotacoes/ListaCompras';
import { ReconhecerProdutoCamera } from '../../components/reconhecimento/ReconhecerProdutoCamera';

interface ProdutoListagem {
  id: number;
  codigo_interno: string | null;
  ean: string | null;
  referencia: string | null;
  descricao: string;
  grupo_id: number | null;
  grupo_nome: string | null;
  categoria_id: number | null;
  categoria_nome: string | null;
  marca_nome: string | null;
  unidade_sigla: string | null;
  preco_custo: string;
  preco_pix: string;
  preco_cartao: string;
  // Meta de markup que vale pro produto (categoria, senao a padrao da loja) — Metas de Margem.
  markup_meta: string | null;
  estoque_atual: string;
  estoque_efetivo: string | null;  // proprio + pai x fator (DA-261). null em produto sem vinculo de filho.
  estoque_minimo: string;
  localizacao: string | null;
  status: string;
  permite_fracionado: boolean | null;
  foto_url: string | null;
  postar_catalogo: boolean;
  uso_consumo: boolean;
  // Compra em aberto (DA-368): comprado, a caminho, ainda sem credito de estoque. Some da
  // lista por padrao (checkbox "Mostrar a caminho"); quando aparece, o selo usa esses campos.
  compra_em_aberto: boolean;
  compra_em_aberto_qtd: string | null;
  compra_em_aberto_emb?: string | null;
  ultima_compra_em: string | null;
  ultima_compra_fornecedor_nome: string | null;
  // Em quantas vendas saiu nos ultimos 30 dias (mesma conta do Mais Vendidos). Botao da ULT. COMPRA.
  vendas_30d: string | null;
  // Ultima Conferencia de estoque (DA-597). null = nunca conferido.
  conferido_em: string | null;
}

interface OpAux { id: number; nome: string; produtos_ativos?: number }
const comQtd = (nome: string, n: number | null | undefined) => (n == null ? nome : `${nome} (${n})`);

// Valor do filtro de Categoria que significa "(sem categoria)" — nenhum id real e' negativo. Vira
// `sem_categoria=1` na busca ([DA-761]). A tela Metas de Margem grava o mesmo valor no sessionStorage.
export const CATEGORIA_NENHUMA = -1;

type ColunaId =
  | 'foto' | 'codigo' | 'descricao' | 'marca' | 'un' | 'custo' | 'pix' | 'mrg' | 'estoque' | 'minimo'
  | 'local' | 'categoria' | 'conferencia' | 'compra' | 'catalogo';

// Caixa "Visualizar Coluna" do cabecalho: nasce marcada; desmarcar esconde a coluna. Pedido do
// Ruan (26/09/2026): a coluna oculta so volta ao fechar e reabrir a tela — por isso o estado
// mora num useState, de proposito FORA do sessionStorage dos filtros. stopPropagation porque
// os cabecalhos de Estoque e Min. Compra ordenam no clique.
function VisualizarColuna({ onOcultar }: { onOcultar: () => void }) {
  return (
    <input
      type="checkbox"
      checked
      title="Visualizar Coluna"
      aria-label="Visualizar Coluna"
      onClick={(e) => e.stopPropagation()}
      onChange={onOcultar}
      style={{ cursor: 'pointer', margin: 0 }}
    />
  );
}

// Persiste filtros entre navegacoes (editar produto e voltar) via sessionStorage.
// Limpa quando a aba fecha. Volta pro padrao se o JSON corromper.
const KEY_FILTROS = 'produtos.filtros';
// Posicao do scroll guardada ao abrir um produto, pra restaurar ao voltar.
const KEY_SCROLL = 'produtos.scroll';
// Linha que estava marcada ao abrir um produto, pra re-destacar ao voltar.
const KEY_SELECTED = 'produtos.selecionado';
interface FiltrosSalvos {
  busca: string;
  // "Busca Identica" (03/11/2026): cada palavra so casa INTEIRA — CADEIRA nao traz ABRACADEIRA.
  buscaIdentica: boolean;
  grupoId: number | '';
  categoriaId: number | '';
  marcaId: number | '';
  mostrarInativos: boolean;
  soCatalogo: boolean;
  soEstoqueMinimo: boolean;
  soUsoConsumo: boolean;
  // Caixa ao lado do selo "N sem" da coluna ESTOQUE: so os de estoque efetivo <= 0 (filtro no banco).
  soSemEstoque: boolean;
  // "Mostrar a caminho" (DA-368): default DESMARCADO — itens com compra_em_aberto=true ficam
  // ocultos, padrao "Mostrar finalizados". Marcado, passa incluir_a_caminho=1 no fetch.
  mostrarACaminho: boolean;
  ordemEstoque: 'asc' | 'desc' | null;
  ordemMinimo: 'asc' | 'desc' | null;
  // Ordem NATURAL pela descricao ('1MM' antes de '10MM') — pedido do Ruan, 06/10/2026.
  ordemDescricao: 'asc' | 'desc' | null;
  // Filtro de margem (Metas de Margem): roda no BANCO, por causa do teto de 500 da lista.
  margem: '' | 'abaixo_meta' | 'sem_custo';
}
function lerFiltros(): FiltrosSalvos {
  const def: FiltrosSalvos = { busca: '', buscaIdentica: false, grupoId: '', categoriaId: '', marcaId: '', mostrarInativos: false, soCatalogo: false, soEstoqueMinimo: false, soUsoConsumo: false, soSemEstoque: false, mostrarACaminho: false, ordemEstoque: null, ordemMinimo: null, ordemDescricao: null, margem: '' };
  try {
    const raw = sessionStorage.getItem(KEY_FILTROS);
    if (raw) return { ...def, ...(JSON.parse(raw) as Partial<FiltrosSalvos>) };
  } catch {}
  return def;
}

export function ProdutosLista() {
  const navigate = useNavigate();
  const toast = useToast();
  const [itens, setItens] = useState<ProdutoListagem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState(() => lerFiltros().busca);
  const [buscaIdentica, setBuscaIdentica] = useState(() => lerFiltros().buscaIdentica);
  const [grupoId, setGrupoId] = useState<number | ''>(() => lerFiltros().grupoId);
  const [categoriaId, setCategoriaId] = useState<number | ''>(() => lerFiltros().categoriaId);
  const [marcaId, setMarcaId] = useState<number | ''>(() => lerFiltros().marcaId);
  const [mostrarInativos, setMostrarInativos] = useState(() => lerFiltros().mostrarInativos);
  const [soCatalogo, setSoCatalogo] = useState(() => lerFiltros().soCatalogo);
  const [soEstoqueMinimo, setSoEstoqueMinimo] = useState(() => lerFiltros().soEstoqueMinimo);
  const [soUsoConsumo, setSoUsoConsumo] = useState(() => lerFiltros().soUsoConsumo);
  const [soSemEstoque, setSoSemEstoque] = useState(() => lerFiltros().soSemEstoque);
  const [mostrarACaminho, setMostrarACaminho] = useState(() => lerFiltros().mostrarACaminho);
  const [margem, setMargem] = useState<FiltrosSalvos['margem']>(() => lerFiltros().margem);
  const [grupos, setGrupos] = useState<OpAux[]>([]);
  const [categorias, setCategorias] = useState<OpAux[]>([]);
  const [semCategoriaQtd, setSemCategoriaQtd] = useState<number | null>(null);
  const [marcas, setMarcas] = useState<OpAux[]>([]);
  const [ordemEstoque, setOrdemEstoque] = useState<'asc' | 'desc' | null>(() => lerFiltros().ordemEstoque);
  const [ordemMinimo, setOrdemMinimo] = useState<'asc' | 'desc' | null>(() => lerFiltros().ordemMinimo);
  const [ordemDescricao, setOrdemDescricao] = useState<'asc' | 'desc' | null>(() => lerFiltros().ordemDescricao);
  // Linha cujo input de Min. Compra esta com o cursor — pinta de amarelo pra o operador
  // ver onde esta enquanto desce a coluna com Enter.
  const [minimoFocado, setMinimoFocado] = useState<number | null>(null);
  // Totais do topo (quantos produtos ainda estao sem Minimo de Compra e sem estoque).
  // Vem do BACKEND (COUNT no banco inteiro, nao na fatia de 500), pra dimensionar a
  // faxina de minimos do modulo Estoque. null enquanto nao carregou.
  const [resumo, setResumo] = useState<{ sem_minimo: number; sem_estoque: number } | null>(null);
  const [colunasOcultas, setColunasOcultas] = useState<Set<ColunaId>>(() => new Set());
  const vis = (c: ColunaId) => !colunasOcultas.has(c);
  const ocultar = (c: ColunaId) => setColunasOcultas((prev) => new Set(prev).add(c));
  // Cabecalho padrao: o rotulo em cima, a caixa embaixo, os dois do lado em que a coluna alinha
  // (pedido dele, com print: cabecalho alinhado no topo e a caixa debaixo do texto).
  const cab = (c: ColunaId, rotulo: ReactNode, alinhar: 'flex-start' | 'center' | 'flex-end' = 'flex-start') => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: alinhar, gap: 3 }}>
      {rotulo}
      <VisualizarColuna onOcultar={() => ocultar(c)} />
    </div>
  );

  const { usuario } = useAuth();
  const podeEditarPreco = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
  // MODO EMERGENCIA (lib/emergencia.ts): a lista vira consulta — o que grava fica desabilitado.
  const emergencia = useEmergencia();
  // Selecao em lote: a MESMA marcacao de linhas serve a duas acoes — preco ou categoria ([DA-761]).
  // Cada botao de entrada liga o seu modo, e o rodape do modo mostra so a acao dele.
  const [acaoLote, setAcaoLote] = useState<'precos' | 'categoria' | null>(null);
  const modoSelecao = acaoLote !== null;
  const [modalCategoria, setModalCategoria] = useState(false);
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
  const [modalPrecos, setModalPrecos] = useState(false);
  // Janela do botao "N vendas · 30d" (id do produto) e, a partir dela, o Enviar p/ Cotacao.
  const [vendas30d, setVendas30d] = useState<number | null>(null);
  const [enviarCotacao, setEnviarCotacao] = useState<{ produto_id: number; descricao: string } | null>(null);

  // As ordenacoes de coluna sao mutuamente exclusivas (uma coluna ordena por vez):
  // ligar uma zera as outras, espelhando o backend que so aceita um ordem_* de cada vez.
  function alternarOrdemEstoque() {
    setOrdemMinimo(null);
    setOrdemDescricao(null);
    setOrdemEstoque((cur) => (cur === null ? 'asc' : cur === 'asc' ? 'desc' : null));
  }
  function alternarOrdemMinimo() {
    setOrdemEstoque(null);
    setOrdemDescricao(null);
    setOrdemMinimo((cur) => (cur === null ? 'asc' : cur === 'asc' ? 'desc' : null));
  }
  function alternarOrdemDescricao() {
    setOrdemEstoque(null);
    setOrdemMinimo(null);
    setOrdemDescricao((cur) => (cur === null ? 'asc' : cur === 'asc' ? 'desc' : null));
  }

  // Abre o produto pra edicao guardando a posicao atual do scroll, pra restaurar
  // exatamente no mesmo ponto ao voltar (em vez de cair no topo da lista).
  function abrirProduto(id: number) {
    const cont = document.querySelector('.app-content') as HTMLElement | null;
    if (cont) sessionStorage.setItem(KEY_SCROLL, String(cont.scrollTop));
    sessionStorage.setItem(KEY_SELECTED, String(id));
    navigate(`/produtos/${id}`);
  }

  // Edicao da DATA da compra pelo selo "Chegando" (DA-368) — mesma peca da cotacao. Grava por
  // produto; rebate o selo na hora com o valor que o backend devolve.
  const [edicaoData, setEdicaoData] = useState<AlvoEdicaoData | null>(null);
  const [salvandoData, setSalvandoData] = useState(false);

  async function confirmarDataCompra(dataISO: string) {
    const alvo = edicaoData;
    if (!alvo) return;
    setSalvandoData(true);
    try {
      const r = await api.patch<{ ultima_compra_em: string | null }>(`/api/produtos/${alvo.produtoId}/ultima-compra`, { data: dataISO });
      setItens((prev) => prev.map((p) => (p.id === alvo.produtoId ? { ...p, ultima_compra_em: r.ultima_compra_em } : p)));
      setEdicaoData(null);
      toast.sucesso('Data da compra atualizada.');
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvandoData(false);
    }
  }

  // Salva o Minimo de Compra de uma linha (PATCH whitelist, ja loga no historico). Otimista: pinta
  // o novo valor na hora (recalcula a flag ▲ e o filtro "Min. Compra"), reconcilia com o que o
  // servidor devolve, e volta ao anterior se falhar.
  async function salvarMinimo(id: number, novo: string) {
    const anterior = itens.find((p) => p.id === id)?.estoque_minimo ?? '0';
    setItens((prev) => prev.map((p) => (p.id === id ? { ...p, estoque_minimo: novo } : p)));
    try {
      const atualizado = await api.patch<{ estoque_minimo: string }>(`/api/produtos/${id}`, { estoque_minimo: novo });
      setItens((prev) => prev.map((p) => (p.id === id ? { ...p, estoque_minimo: atualizado.estoque_minimo } : p)));
      // Mantem o contador "sem minimo" do topo vivo: cruzar 0 -> N tira um da conta,
      // voltar N -> 0 devolve. O numero cai conforme a faxina avanca, sem refetch.
      const antes0 = (Number(anterior) || 0) === 0;
      const depois0 = (Number(atualizado.estoque_minimo) || 0) === 0;
      if (antes0 !== depois0) {
        setResumo((r) => (r ? { ...r, sem_minimo: r.sem_minimo + (depois0 ? 1 : -1) } : r));
      }
    } catch (e: any) {
      toast.erro(e.message);
      setItens((prev) => prev.map((p) => (p.id === id ? { ...p, estoque_minimo: anterior } : p)));
    }
  }

  function toggleSel(id: number) {
    setSelecionados((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }
  function sairSelecao() {
    setAcaoLote(null);
    setSelecionados(new Set());
  }

  // Filtro "Estoque Minimo": so produtos com estoque EFETIVO <= Estoque Minimo.
  // Mede pelo efetivo (proprio + pai x fator) pra um filho coberto pelo pai nao
  // pedir reposicao a toa — espelha o auto-fracionamento do caixa (DA-261). Client-side.
  const itensFiltrados = useMemo(() => {
    let base = itens;
    // "So uso e consumo": mostra so os itens de consumo proprio (escondidos da venda).
    // Client-side, igual o "Estoque Minimo"; o contador do topo reflete o filtrado.
    if (soUsoConsumo) base = base.filter((p) => p.uso_consumo);
    if (soEstoqueMinimo) base = base.filter((p) => Number(p.estoque_efetivo ?? p.estoque_atual) <= Number(p.estoque_minimo));
    return base;
  }, [itens, soEstoqueMinimo, soUsoConsumo]);

  const itensOrdenados = useMemo(() => {
    // Descricao em ordem NATURAL (o numero do nome comparado como numero) — o ORDER BY do banco
    // nao faz isso, entao a tela ordena; o backend so devolve a lista inteira (ordem_descricao).
    if (ordemDescricao) {
      return [...itensFiltrados].sort((a, b) => {
        const c = compararDescricaoNatural(a.descricao, b.descricao);
        return ordemDescricao === 'asc' ? c : -c;
      });
    }
    if (!ordemEstoque) return itensFiltrados;
    return [...itensFiltrados].sort((a, b) => {
      const va = Number(a.estoque_efetivo ?? a.estoque_atual) || 0;
      const vb = Number(b.estoque_efetivo ?? b.estoque_atual) || 0;
      return ordemEstoque === 'asc' ? va - vb : vb - va;
    });
  }, [itensFiltrados, ordemEstoque, ordemDescricao]);

  const produtosSelecionados = useMemo(
    () => itens.filter((p) => selecionados.has(p.id)),
    [itens, selecionados],
  );
  const todosVisiveisSelecionados = itensOrdenados.length > 0 && itensOrdenados.every((p) => selecionados.has(p.id));
  function toggleTodos() {
    setSelecionados((s) => {
      const n = new Set(s);
      if (itensOrdenados.length > 0 && itensOrdenados.every((p) => n.has(p.id))) {
        itensOrdenados.forEach((p) => n.delete(p.id));
      } else {
        itensOrdenados.forEach((p) => n.add(p.id));
      }
      return n;
    });
  }

  // Comportamento padrao de grid do sistema (hook compartilhado): clicar marca a
  // linha, ↑/↓ navegam, Enter/duplo-clique abrem o produto, e a linha selecionada
  // acompanha o scroll. Desligado no modo de selecao em lote (la o clique marca o
  // checkbox). Selecao por id, entao sobrevive ao auto-refresh da lista.
  const grid = useGridNavegavel<ProdutoListagem>({
    itens: itensOrdenados,
    getId: (p) => p.id,
    onAtivar: (p) => abrirProduto(p.id),
    // Com a janela de vendas ou a de cotacao aberta, Enter/setas nao podem agir na lista de tras.
    ativo: !modoSelecao && vendas30d == null && !enviarCotacao,
  });

  useEffect(() => {
    Promise.all([
      api.get<OpAux[]>('/api/grupos'),
      api.get<OpAux[]>('/api/marcas'),
    ]).then(([g, m]) => { setGrupos(g); setMarcas(m); }).catch(() => {});
    carregarCategorias();
  }, []);

  // Categorias + quantos produtos ativos em cada (e sem categoria) — o numero vai entre parenteses
  // no filtro. Recarrega depois do "Alterar categoria em lote", que muda as contas.
  function carregarCategorias() {
    api.get<OpAux[]>('/api/categorias').then(setCategorias).catch(() => {});
    api.get<{ ativos: number }>('/api/categorias/sem-vinculo').then((r) => setSemCategoriaQtd(r.ativos)).catch(() => {});
  }

  async function carregar() {
    setCarregando(true);
    try {
      const lista = await api.get<ProdutoListagem[]>('/api/produtos', {
        query: {
          q: busca || undefined,
          busca_identica: buscaIdentica ? '1' : undefined,
          grupo_id: grupoId || undefined,
          categoria_id: categoriaId !== '' && categoriaId !== CATEGORIA_NENHUMA ? categoriaId : undefined,
          sem_categoria: categoriaId === CATEGORIA_NENHUMA ? '1' : undefined,
          marca_id: marcaId || undefined,
          todos: mostrarInativos || undefined,
          catalogo: soCatalogo || undefined,
          // Compra em aberto (DA-368): a tela Produtos pede pro backend esconder por padrao
          // (esconder_a_caminho=1) e so larga a mao (incluir_a_caminho=1) quando o checkbox
          // "Mostrar a caminho" liga. Os dois nunca vao juntos como "mostrar" — um deles manda.
          // BUSCA ATIVA sempre mostra: buscar um produto pelo nome/codigo nao tem nada a ver com
          // comprar (o cadastro serve pra mil coisas) — esconder o que se procurou de proposito e
          // "perder" o produto. So a varredura por estoque (sem busca) esconde os a caminho.
          esconder_a_caminho: (mostrarACaminho || busca.trim() !== '') ? undefined : '1',
          incluir_a_caminho: (mostrarACaminho || busca.trim() !== '') ? '1' : undefined,
          // Quando ordena por estoque, o backend devolve a lista inteira ja
          // ordenada (sem o corte alfabetico de 500). Sem isso, a ordenacao so
          // veria a fatia carregada. Idem pro Minimo de Compra (exclusivos).
          ordem_estoque: ordemEstoque || undefined,
          ordem_minimo: ordemMinimo || undefined,
          ordem_descricao: ordemDescricao || undefined,
          margem: margem || undefined,
          sem_estoque: soSemEstoque ? '1' : undefined,
          com_vendas_30d: '1',
        },
      });
      setItens(lista);
    } catch (e: any) {
      toast.erro(e.message);
    } finally { setCarregando(false); }
  }

  useEffect(() => {
    const t = setTimeout(carregar, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, buscaIdentica, grupoId, categoriaId, marcaId, mostrarInativos, soCatalogo, mostrarACaminho, ordemEstoque, ordemMinimo, ordemDescricao, margem, soSemEstoque]);

  // Totais "sem minimo / sem estoque" pelos MESMOS filtros da lista (menos a ordenacao,
  // que nao muda o conjunto). Endpoint proprio que conta no banco inteiro — o COUNT nao
  // sofre o teto de 500 que truncaria uma contagem feita na tela.
  useEffect(() => {
    const t = setTimeout(() => {
      api.get<{ sem_minimo: number; sem_estoque: number }>('/api/produtos/resumo-estoque', {
        query: {
          q: busca || undefined,
          busca_identica: buscaIdentica ? '1' : undefined,
          grupo_id: grupoId || undefined,
          categoria_id: categoriaId !== '' && categoriaId !== CATEGORIA_NENHUMA ? categoriaId : undefined,
          sem_categoria: categoriaId === CATEGORIA_NENHUMA ? '1' : undefined,
          marca_id: marcaId || undefined,
          todos: mostrarInativos || undefined,
          catalogo: soCatalogo || undefined,
          margem: margem || undefined,
          // Mesma regra do "a caminho" da lista: o selo "N sem" tem ao lado a caixa que filtra
          // esses itens, entao ele tem que contar o conjunto que a lista mostra.
          esconder_a_caminho: (mostrarACaminho || busca.trim() !== '') ? undefined : '1',
          incluir_a_caminho: (mostrarACaminho || busca.trim() !== '') ? '1' : undefined,
        },
      }).then(setResumo).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [busca, buscaIdentica, grupoId, categoriaId, marcaId, mostrarInativos, soCatalogo, margem, mostrarACaminho]);

  // Persiste filtros (incluindo ordenacao) pra sobreviver a F5 e retorno de edicao.
  useEffect(() => {
    sessionStorage.setItem(
      KEY_FILTROS,
      JSON.stringify({ busca, buscaIdentica, grupoId, categoriaId, marcaId, mostrarInativos, soCatalogo, soEstoqueMinimo, soUsoConsumo, soSemEstoque, mostrarACaminho, ordemEstoque, ordemMinimo, ordemDescricao, margem } satisfies FiltrosSalvos),
    );
  }, [busca, buscaIdentica, grupoId, categoriaId, marcaId, mostrarInativos, soCatalogo, soEstoqueMinimo, soUsoConsumo, soSemEstoque, mostrarACaminho, ordemEstoque, ordemMinimo, ordemDescricao, margem]);

  // Restaura a posicao do scroll ao voltar da edicao, depois que a lista carregou.
  // Consome as chaves (remove) pra so restaurar uma vez — visita nova cai no topo.
  useEffect(() => {
    if (carregando) return;
    const raw = sessionStorage.getItem(KEY_SCROLL);
    if (raw == null) return;
    sessionStorage.removeItem(KEY_SCROLL);
    const y = Number(raw) || 0;
    // Re-destaca a linha que estava marcada antes de abrir o produto (consome 1x).
    const sel = sessionStorage.getItem(KEY_SELECTED);
    if (sel != null) {
      sessionStorage.removeItem(KEY_SELECTED);
      grid.setSelectedId(Number(sel) || null);
    }
    requestAnimationFrame(() => {
      const cont = document.querySelector('.app-content') as HTMLElement | null;
      if (cont) cont.scrollTop = y;
    });
  }, [carregando]);

  const sticky = useStickyHeader(140);
  // Cabecalhos alinhados no TOPO: com rotulos de 1 e 2 linhas, o meio deixava cada caixa numa altura.
  const thTopo = { ...sticky.thStyle, verticalAlign: 'top' as const };

  return (
    <div>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo">Produtos <span className="muted" style={{ fontSize: 14, fontWeight: 400 }}>({itensOrdenados.length})</span></h2>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)} />
              Mostrar inativos
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }} title="Mostrar apenas produtos marcados para o catalogo publico">
              <input type="checkbox" checked={soCatalogo} onChange={(e) => setSoCatalogo(e.target.checked)} />
              Só do catálogo
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }} title="Mostrar apenas produtos com Est. Atual menor ou igual ao Minimo de Compra (precisam ser comprados)">
              <input type="checkbox" checked={soEstoqueMinimo} onChange={(e) => setSoEstoqueMinimo(e.target.checked)} />
              Mín. Compra
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }} title="Mostrar apenas itens de uso/consumo da loja (escondidos da venda)">
              <input type="checkbox" checked={soUsoConsumo} onChange={(e) => setSoUsoConsumo(e.target.checked)} />
              Só uso e consumo
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }} title="Produtos ja comprados (compra em aberto) ficam ocultos por padrao — ja estao a caminho, nao precisam comprar de novo">
              <input type="checkbox" checked={mostrarACaminho} onChange={(e) => setMostrarACaminho(e.target.checked)} />
              Mostrar a caminho
            </label>
          </div>
          <div className="pagina-acoes" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {modoSelecao ? (
              <>
                <span className="muted" style={{ fontSize: 13 }}>{produtosSelecionados.length} selecionado(s)</span>
                <button
                  type="button"
                  className="btn-primario"
                  disabled={produtosSelecionados.length === 0}
                  title={produtosSelecionados.length === 0 ? 'Marque os produtos na lista' : undefined}
                  onClick={() => (acaoLote === 'categoria' ? setModalCategoria(true) : setModalPrecos(true))}
                >
                  {acaoLote === 'categoria' ? 'Alterar categoria' : 'Alterar preços'} ({produtosSelecionados.length})
                </button>
                <button type="button" className="btn-secundario" onClick={sairSelecao}>Cancelar</button>
              </>
            ) : (
              <>
                {podeEditarPreco && (
                  <button type="button" className="btn-secundario" onClick={() => setAcaoLote('categoria')} disabled={emergencia} title={emergencia ? MSG_EMERGENCIA : undefined}>
                    Alterar categoria em lote
                  </button>
                )}
                {podeEditarPreco && (
                  <button type="button" className="btn-secundario" onClick={() => setAcaoLote('precos')} disabled={emergencia} title={emergencia ? MSG_EMERGENCIA : undefined}>
                    Alterar preços em lote
                  </button>
                )}
                <button type="button" className="btn-primario" onClick={() => navigate('/produtos/novo')} disabled={emergencia} title={emergencia ? MSG_EMERGENCIA : undefined}>+ Novo</button>
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <label
            style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, whiteSpace: 'nowrap' }}
            title="Marcada, cada palavra digitada so encontra a palavra INTEIRA: CADEIRA nao traz ABRACADEIRA"
          >
            <input type="checkbox" checked={buscaIdentica} onChange={(e) => setBuscaIdentica(e.target.checked)} />
            Busca Idêntica
          </label>
          <div style={{ position: 'relative', flex: 1, minWidth: 280 }}>
            <input
              type="search"
              placeholder="Buscar codigo, descricao, marca, grupo, categoria, EAN..."
              className="busca"
              style={{ width: '100%', paddingRight: 42 }}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
            {/* A camera e a mesma PORTA ALTERNATIVA do orcamento (DA-392), pra quem tem a peca
                na mao e nao sabe como a loja a cadastrou. Aqui ela nao abre a ficha: PREENCHE
                A BUSCA com o codigo, como se tivesse sido digitado — a lista continua sendo a
                resposta, e o operador ve o produto no meio dos irmaos (a camera acerta a
                FAMILIA e nao le a MEDIDA). Sem `somenteVendaveis`: o cadastro mostra tudo,
                inclusive Uso/Consumo. */}
            <ReconhecerProdutoCamera
              tela="produtos"
              onEscolher={(p) => setBusca(p.codigo_interno ?? p.descricao)}
            />
          </div>
          <div style={{ minWidth: 200 }}>
            <ComboBox
              opcoes={[{ value: '', label: 'Todos os grupos' }, ...grupos.map((g) => ({ value: g.id, label: g.nome }))]}
              value={grupoId}
              onChange={(v) => setGrupoId(v === '' ? '' : Number(v))}
              placeholder="Grupo"
            />
          </div>
          <div style={{ minWidth: 200 }}>
            <ComboBox
              opcoes={[{ value: '', label: 'Todas as categorias' }, { value: CATEGORIA_NENHUMA, label: comQtd('(sem categoria)', semCategoriaQtd) }, ...categorias.map((c) => ({ value: c.id, label: comQtd(c.nome, c.produtos_ativos) }))]}
              value={categoriaId}
              onChange={(v) => setCategoriaId(v === '' ? '' : Number(v))}
              placeholder="Categoria"
            />
          </div>
          <div style={{ minWidth: 200 }}>
            <ComboBox
              opcoes={[{ value: '', label: 'Todas as marcas' }, ...marcas.map((m) => ({ value: m.id, label: m.nome }))]}
              value={marcaId}
              onChange={(v) => setMarcaId(v === '' ? '' : Number(v))}
              placeholder="Marca"
            />
          </div>
          {/* Metas de Margem: a meta de cada produto e' a da categoria dele (ou a padrao da loja),
              definida em Cadastros > Metas de Margem. Filtra no banco (a lista tem teto de 500). */}
          <div style={{ minWidth: 190 }} title="Margem = markup sobre o custo, a mesma conta do MRG PIX. A meta de cada produto vem da categoria (ou da meta padrao da loja).">
            <ComboBox
              opcoes={[
                { value: '', label: 'Todas as margens' },
                { value: 'abaixo_meta', label: 'Abaixo da meta' },
                { value: 'sem_custo', label: 'Sem custo' },
              ]}
              value={margem}
              onChange={(v) => setMargem((v || '') as FiltrosSalvos['margem'])}
              placeholder="Margem"
            />
          </div>
        </div>
      </div>

      {carregando ? <p className="muted">Carregando...</p> : itensOrdenados.length === 0 ? <p className="muted">Nenhum produto.</p> : (
        <table className="tabela tabela-compacta" style={sticky.tableStyle}>
          <thead><tr>
            {modoSelecao && (
              <th style={{ ...thTopo, width: 36, textAlign: 'center' }}>
                <input type="checkbox" checked={todosVisiveisSelecionados} onChange={toggleTodos} aria-label="Selecionar todos" />
              </th>
            )}
            {vis('foto') && <th style={{ ...thTopo, width: 70 }}>{cab('foto', 'FOTO')}</th>}
            {vis('codigo') && <th style={{ ...thTopo, width: 80 }}>{cab('codigo', 'COD.')}</th>}
            {vis('descricao') && <th
              style={{ ...thTopo, cursor: 'pointer', userSelect: 'none' }}
              onClick={alternarOrdemDescricao}
              title="Clique para ordenar pela descricao, com a medida do nome do menor para o maior (asc / desc / sem ordem)"
            >
              {cab('descricao', (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  DESCRICAO
                  {ordemDescricao === 'asc' ? <ChevronUp size={12} /> : ordemDescricao === 'desc' ? <ChevronDown size={12} /> : <ChevronsUpDown size={12} style={{ opacity: 0.4 }} />}
                </span>
              ))}
            </th>}
            {vis('marca') && <th style={thTopo}>{cab('marca', 'MARCA')}</th>}
            {vis('un') && <th style={thTopo}>{cab('un', 'UN.')}</th>}
            {vis('custo') && <th style={{ ...thTopo, textAlign: 'right' }}>{cab('custo', 'CUSTO (R$)', 'flex-end')}</th>}
            {vis('pix') && <th style={{ ...thTopo, textAlign: 'right' }}>{cab('pix', 'PIX (R$)', 'flex-end')}</th>}
            {vis('mrg') && <th style={{ ...thTopo, textAlign: 'right' }}>{cab('mrg', 'MRG PIX', 'flex-end')}</th>}
            {vis('estoque') && <th
              style={{ ...thTopo, textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
              onClick={alternarOrdemEstoque}
              title="Clique para alternar ordenacao (asc / desc / sem ordem)"
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  ESTOQUE
                  {ordemEstoque === 'asc' ? <ChevronUp size={12} /> : ordemEstoque === 'desc' ? <ChevronDown size={12} /> : <ChevronsUpDown size={12} style={{ opacity: 0.4 }} />}
                </span>
                {resumo && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <span title="Produtos sem estoque (efetivo zerado ou negativo)"
                      style={{ fontSize: '0.62rem', fontWeight: 700, color: '#000', backgroundColor: '#f97316', padding: '0 6px', borderRadius: 9999, whiteSpace: 'nowrap' }}>
                      {resumo.sem_estoque.toLocaleString('pt-BR')} sem
                    </span>
                    {/* Filtra a lista pelos itens do selo. stopPropagation: o cabecalho ordena no clique. */}
                    <input
                      type="checkbox"
                      checked={soSemEstoque}
                      title="Mostrar só os produtos sem estoque"
                      aria-label="Mostrar só os produtos sem estoque"
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setSoSemEstoque(e.target.checked)}
                      style={{ cursor: 'pointer', margin: 0 }}
                    />
                  </span>
                )}
                <VisualizarColuna onOcultar={() => ocultar('estoque')} />
              </div>
            </th>}
            {vis('minimo') && <th
              style={{ ...thTopo, textAlign: 'right', width: 90, cursor: 'pointer', userSelect: 'none' }}
              onClick={alternarOrdemMinimo}
              title="Clique para ordenar por Minimo de Compra (asc / desc / sem ordem). Abaixo dele o produto entra na Lista de Compras"
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                  MÍN. COMPRA
                  {ordemMinimo === 'asc' ? <ChevronUp size={12} /> : ordemMinimo === 'desc' ? <ChevronDown size={12} /> : <ChevronsUpDown size={12} style={{ opacity: 0.4 }} />}
                </span>
                {/* Caixa de ocultar a coluna AO LADO do selo (pedido dele, 28/10/2026: "ganhariamos mais
                    uma linha" no cabecalho). ⚠️ Na coluna vizinha, ESTOQUE, a caixa nesse lugar e' o
                    FILTRO "so sem estoque" — aqui e' o ocultar; avisado a ele antes. */}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {resumo && (
                    <span title="Produtos sem Minimo de Compra definido"
                      style={{ fontSize: '0.62rem', fontWeight: 700, color: '#000', backgroundColor: '#f97316', padding: '0 6px', borderRadius: 9999, whiteSpace: 'nowrap' }}>
                      {resumo.sem_minimo.toLocaleString('pt-BR')} sem
                    </span>
                  )}
                  <VisualizarColuna onOcultar={() => ocultar('minimo')} />
                </span>
              </div>
            </th>}
            {vis('local') && <th style={thTopo}>{cab('local', 'LOCAL')}</th>}
            {vis('categoria') && <th style={thTopo}>{cab('categoria', 'CATEGORIA')}</th>}
            {vis('conferencia') && <th style={thTopo} title="Ultima conferencia de estoque (Conferencia dos Vendidos ou ajuste pela ficha)">{cab('conferencia', 'ÚLT. CONFERÊNCIA')}</th>}
            {vis('compra') && <th style={thTopo} title="Ultima compra real (carimbada pela nota de entrada)">{cab('compra', 'ÚLT. COMPRA')}</th>}
            {vis('catalogo') && <th style={{ ...thTopo, textAlign: 'center' }} title="No catalogo publico">{cab('catalogo', 'CATALOGO', 'center')}</th>}
          </tr></thead>
          <tbody>
            {itensOrdenados.map((p) => {
              const focado = !modoSelecao && p.id === minimoFocado;
              const rowAttrs = modoSelecao
                ? {
                    onClick: () => toggleSel(p.id),
                    style: { cursor: 'pointer', ...(selecionados.has(p.id) ? { backgroundColor: '#fde68a' } : {}) },
                  }
                : grid.rowProps(p);
              // Cursor num input de Min. Compra pinta a linha (mesmo amarelo daltonismo-safe da
              // selecao do grid), pra o operador nao se perder ao descer a coluna com Enter.
              const style = focado
                ? { ...rowAttrs.style, background: '#fde68a', color: '#000', boxShadow: 'inset 3px 0 0 var(--accent)' }
                : rowAttrs.style;
              return (
              <tr key={p.id} {...rowAttrs} style={style}>
                {modoSelecao && (
                  <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selecionados.has(p.id)} onChange={() => toggleSel(p.id)} />
                  </td>
                )}
                {vis('foto') && <td>{p.foto_url ? <img src={p.foto_url} alt="" className="produto-thumb" loading="lazy" /> : <div className="produto-thumb-vazio" />}</td>}
                {vis('codigo') && <td><span className="codigo-curto">{p.codigo_interno ?? '—'}</span></td>}
                {vis('descricao') && <td>
                  <div className="cel-desc-titulo">
                    {p.descricao}
                    {p.uso_consumo && (
                      <span
                        title="Item de uso/consumo da loja — nao aparece na venda"
                        style={{ marginLeft: 6, backgroundColor: '#fde68a', color: '#000', padding: '1px 6px', borderRadius: 9999, fontSize: '0.65rem', fontWeight: 700, verticalAlign: 'middle', whiteSpace: 'nowrap' }}
                      >
                        USO/CONSUMO
                      </span>
                    )}
                  </div>
                  {p.grupo_nome && (
                    <div className="cel-desc-sub">{p.grupo_nome}</div>
                  )}
                </td>}
                {vis('marca') && <td>{p.marca_nome ?? '—'}</td>}
                {vis('un') && <td>{p.unidade_sigla ?? '—'}</td>}
                {/* Sem o "R$ " (o cabecalho ja diz "(R$)") — largura devolvida a DESCRICAO, pedido dele 28/10/2026. */}
                {vis('custo') && <td className="cel-num" style={{ textAlign: 'right' }}>{formatMoedaSemSimbolo(p.preco_custo, 4)}</td>}
                {vis('pix') && <td className="cel-num-forte" style={{ textAlign: 'right' }}>{formatMoedaSemSimbolo(p.preco_pix, 4)}</td>}
                {vis('mrg') && <td className="cel-num" style={{ textAlign: 'right' }}>
                  {/* ▼ + negrito quando abaixo da meta — marcador de FORMA, nao de cor ([[user_daltonismo]];
                      e o amarelo ja e' a linha selecionada). Mesma regra do filtro, via shared. */}
                  {p.markup_meta != null && abaixoDaMeta(p.preco_pix, p.preco_custo, Number(p.markup_meta)) ? (
                    <span style={{ fontWeight: 700 }} title={`Abaixo da meta de ${Number(p.markup_meta).toLocaleString('pt-BR')}%`}>
                      ▼ {formatMargem(p.preco_pix, p.preco_custo)}
                    </span>
                  ) : formatMargem(p.preco_pix, p.preco_custo)}
                </td>}
                {vis('estoque') && <td className="cel-num" style={{ textAlign: 'right' }}>
                  {Number(p.estoque_efetivo ?? p.estoque_atual) <= Number(p.estoque_minimo) && (
                    <span className="repor-flag" title="Repor: estoque efetivo no minimo ou abaixo">▲ </span>
                  )}
                  <EstoqueEfetivo
                    proprio={Number(p.estoque_atual)}
                    efetivo={p.estoque_efetivo != null ? Number(p.estoque_efetivo) : null}
                  />
                  {p.compra_em_aberto && (
                    <SeloChegando
                      dataISO={p.ultima_compra_em}
                      fornecedorNome={p.ultima_compra_fornecedor_nome ?? null}
                      qtd={p.compra_em_aberto_qtd ?? null}
                      emb={p.compra_em_aberto_emb ?? null}
                      onEditar={(rect) =>
                        setEdicaoData({
                          produtoId: p.id,
                          rect,
                          dataAtualISO: p.ultima_compra_em,
                          fornNome: p.ultima_compra_fornecedor_nome ?? null,
                        })
                      }
                    />
                  )}
                </td>}
                {!vis('minimo') ? null : podeEditarPreco && !modoSelecao && !emergencia ? (
                  // stopPropagation: sem isso, o clique/duplo-clique no input marcaria a linha e abriria
                  // o produto (grid onAtivar), atropelando a edicao.
                  <td
                    className="cel-num"
                    style={{ textAlign: 'right' }}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                  >
                    <InputMinimo
                      valor={p.estoque_minimo}
                      onSalvar={(novo) => salvarMinimo(p.id, novo)}
                      aoFocar={() => setMinimoFocado(p.id)}
                      // So limpa se ainda for esta linha: no Enter, o blur desta corre junto do focus
                      // da proxima — sem o guard, o blur apagaria o amarelo que o focus acabou de por.
                      aoSair={() => setMinimoFocado((cur) => (cur === p.id ? null : cur))}
                    />
                  </td>
                ) : (
                  <td className="cel-num" style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{Number(p.estoque_minimo).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}</td>
                )}
                {vis('local') && <td>{p.localizacao ?? '—'}</td>}
                {vis('categoria') && <td>{p.categoria_nome ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>}
                {vis('conferencia') && <td className="cel-num" style={{ whiteSpace: 'nowrap' }}>
                  {p.conferido_em ? formatDataSP(p.conferido_em) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                </td>}
                {vis('compra') && <td>
                  {/* maxWidth + quebra: numa linha so, razao social longa ("MARTINS COMERCIO E INDUSTRIA
                      EIRELI", 223px) empurrava a tabela pra fora da tela de 1536px (1920 a 125%). */}
                  {p.ultima_compra_em ? (
                    <span
                      title={p.ultima_compra_fornecedor_nome ?? undefined}
                      style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.15, backgroundColor: 'var(--primary)', color: 'var(--accent-fg)', padding: '2px 8px', borderRadius: 12, fontSize: '11.5px', fontWeight: 700, whiteSpace: 'normal', maxWidth: 150 }}
                    >
                      <span>{formatDataSP(p.ultima_compra_em)}</span>
                      {p.ultima_compra_fornecedor_nome && (
                        <span style={{ fontWeight: 600, opacity: 0.85, fontSize: '10px' }}>{p.ultima_compra_fornecedor_nome}</span>
                      )}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--text-muted)' }}>—</span>
                  )}
                  {/* Vendas dos ultimos 30 dias (28/10/2026, pedido dele): o numero aparece direto e o
                      clique abre a linha do relatorio Mais Vendidos deste produto. Par de cor FIXO
                      (branco + preto), pra nao sumir na linha selecionada em amarelo. */}
                  {p.vendas_30d != null && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setVendas30d(p.id);
                      }}
                      onDoubleClick={(e) => e.stopPropagation()}
                      title="Vendas dos ultimos 30 dias — clique para ver a linha do Mais Vendidos"
                      style={{ display: 'block', marginTop: 2, background: '#fff', color: Number(p.vendas_30d) > 0 ? '#000' : '#475569', border: '1px solid #94a3b8', borderRadius: 12, padding: '0 8px', fontSize: '11px', lineHeight: '15px', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}
                    >
                      {Number(p.vendas_30d)} {Number(p.vendas_30d) === 1 ? 'venda' : 'vendas'} · 30d
                    </button>
                  )}
                </td>}
                {vis('catalogo') && <td style={{ textAlign: 'center' }}>
                  {p.postar_catalogo
                    ? <span style={{ backgroundColor: 'var(--positive-bg)', color: 'var(--positive-fg)', padding: '2px 8px', borderRadius: 9999, fontSize: '0.72rem', fontWeight: 700 }}>✓ SIM</span>
                    : <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 500 }}>—</span>}
                </td>}
              </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {vendas30d != null && (
        <ModalVendas30d
          produtoId={vendas30d}
          onFechar={() => setVendas30d(null)}
          onEnviar={(l) => {
            setVendas30d(null);
            setEnviarCotacao({ produto_id: l.id, descricao: l.descricao });
          }}
        />
      )}

      {enviarCotacao && (
        <ModalSelecionarCotacao
          produtoId={enviarCotacao.produto_id}
          produtoDesc={enviarCotacao.descricao}
          onClose={() => setEnviarCotacao(null)}
        />
      )}

      {modalPrecos && (
        <ModalPrecosLote
          produtos={produtosSelecionados}
          onFechar={() => setModalPrecos(false)}
          onConcluido={() => { setModalPrecos(false); sairSelecao(); carregar(); }}
        />
      )}

      {modalCategoria && (
        <ModalCategoriaLote
          produtos={produtosSelecionados}
          categorias={categorias}
          onFechar={() => setModalCategoria(false)}
          onConcluido={() => { setModalCategoria(false); sairSelecao(); carregar(); carregarCategorias(); }}
        />
      )}

      {edicaoData && (
        <EditorDataCompra
          alvo={edicaoData}
          salvando={salvandoData}
          onCancelar={() => setEdicaoData(null)}
          onConfirmar={(d) => void confirmarDataCompra(d)}
        />
      )}
    </div>
  );
}

// Input inline do Minimo de Compra (Fila 1 do modulo Estoque, DA-328). O campo e o backend ja
// existiam; o gargalo era encher em massa — um a um na ficha ninguem preenche centenas. Aqui a
// coluna vira editavel: filtra por grupo, desce a coluna com Enter (o `enter-as-tab` global leva
// pro proximo input = a proxima linha) e a Lista de Compras passa a receber o produto quando cai.
//
// `estoque_minimo=0` = ninguem definiu (92% do catalogo) — mostra vazio com placeholder "—" pra
// convidar a digitar, em vez de um "0" que finge estar preenchido. Parse pt-BR (virgula = decimal);
// nao mexo em separador de milhar porque minimo de compra e quase sempre inteiro pequeno, e o valor
// e semeado CRU (sem toLocaleString) justamente pra "1000" nao voltar como 1 no reparse.
function InputMinimo({ valor, onSalvar, aoFocar, aoSair }: {
  valor: string;
  onSalvar: (novo: string) => void;
  aoFocar: () => void;
  aoSair: () => void;
}) {
  const fmt = (n: number) => (n === 0 ? '' : String(n).replace('.', ','));
  const [texto, setTexto] = useState(() => fmt(Number(valor) || 0));
  // Ressincroniza quando o valor de fora muda (salvou -> servidor devolveu, ou recarregou a lista),
  // mas so com o campo SEM foco: senao apagaria o que o usuario esta digitando.
  const focadoRef = useRef(false);
  useEffect(() => {
    if (!focadoRef.current) setTexto(fmt(Number(valor) || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor]);

  function commit() {
    focadoRef.current = false;
    const atual = Number(valor) || 0;
    const t = texto.trim().replace(',', '.');
    const n = t === '' ? 0 : Number(t);
    if (!Number.isFinite(n) || n < 0) { setTexto(fmt(atual)); return; } // invalido: volta ao valor atual
    if (n === atual) { setTexto(fmt(atual)); return; }                  // nao mudou: normaliza display
    onSalvar(String(n));
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      className="cel-num"
      value={texto}
      placeholder="—"
      title="Abaixo disso o produto entra na Lista de Compras"
      onFocus={(e) => { focadoRef.current = true; aoFocar(); e.currentTarget.select(); }}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => { commit(); aoSair(); }}
      style={{ width: 60, textAlign: 'right', padding: '2px 6px' }}
    />
  );
}
